import { useState, type FormEvent } from "react";
import { Button, EmptyState, Modal, DialogCancel } from "@cntrl/ui";
import { pushToast } from "../../app/toasts.js";
import styles from "./LinkCodeModal.module.scss";

/** The two tabs — vocabulary from the reference design ("Generate code" / "Redeem code"). */
type Tab = "generate" | "redeem";

export interface LinkCodeModalProps {
  /** The dialog's title — each side names what it links: "Link a team" (band) / "Link an artist" (org). */
  title?: string;
  /** "Generate a one-time code, then send it to your …" — each side names its own counterparty. */
  generateHint: string;
  /** "Enter the code your … generated for you." */
  redeemHint: string;
  /** What the success state says once a redemption lands. */
  redeemedNote: string;
  onGenerate: () => Promise<{ code?: string | null; expiresOn?: string | null }>;
  onRedeem: (code: string) => Promise<unknown>;
  generating: boolean;
  redeeming: boolean;
  onClose: () => void;
}

/** "Aug 22, 6:57 PM" — the reference's expiry line; wall-clock local. */
function fmtExpiry(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * "Link a team" (PO, 2026-08-20) — the one-time-code handshake, one modal for BOTH shells: the band
 * generates a code its label/manager/booking agent redeems from their roster, and vice versa. The
 * code is the whole ceremony — no email lookup, no inbox accept; redemption activates the link
 * instantly (typed from the org's own kind, adjustable after via Change type).
 *
 * Presentational: the caller wires its own side's mutations. Errors render inline, verbatim;
 * success is never phantom (each state waits on its promise).
 */
export function LinkCodeModal({
  title = "Link a team",
  generateHint,
  redeemHint,
  redeemedNote,
  onGenerate,
  onRedeem,
  generating,
  redeeming,
  onClose,
}: LinkCodeModalProps) {
  const [tab, setTab] = useState<Tab>("generate");
  const [issued, setIssued] = useState<{ code: string; expiresOn: string | null } | null>(null);
  const [codeInput, setCodeInput] = useState("");
  const [redeemed, setRedeemed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function generate() {
    setError(null);
    onGenerate()
      .then((r) => setIssued({ code: r.code ?? "", expiresOn: r.expiresOn ?? null }))
      .catch((err: Error) => setError(err.message));
  }

  function redeem(e: FormEvent) {
    e.preventDefault();
    setError(null);
    onRedeem(codeInput.trim())
      .then(() => setRedeemed(true))
      .catch((err: Error) => setError(err.message));
  }

  async function copy() {
    if (!issued?.code) return;
    try {
      await navigator.clipboard.writeText(issued.code);
      pushToast("Code copied", "success");
    } catch {
      pushToast("Couldn't copy — select the code and copy it by hand");
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className={styles.body}>
        {/* The two-way switch — a code is either minted here or typed here. */}
        <div className={styles.tabs} role="tablist" aria-label={title}>
          {(["generate", "redeem"] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={styles.tab}
              data-selected={tab === t || undefined}
              onClick={() => {
                setTab(t);
                setError(null);
              }}
            >
              {t === "generate" ? "Generate code" : "Redeem code"}
            </button>
          ))}
        </div>

        {tab === "generate" && !issued && (
          <>
            <p className={styles.hint}>{generateHint}</p>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <footer className={styles.footer}>
              <DialogCancel />
              <Button onClick={generate} loading={generating}>
                Generate code
              </Button>
            </footer>
          </>
        )}

        {tab === "generate" && issued && (
          <>
            <p className={styles.wellLabel} id="link-code-label">
              Your one-time code
            </p>
            <output className={styles.well} htmlFor="link-code-label">
              {issued.code}
            </output>
            <p className={styles.expiry}>
              One-time use{issued.expiresOn ? ` · expires ${fmtExpiry(issued.expiresOn)}` : ""}
            </p>
            <div className={styles.copyRow}>
              <Button variant="ghost" onClick={() => void copy()}>
                Copy code
              </Button>
            </div>
            <footer className={styles.footer}>
              <Button onClick={onClose}>Done</Button>
            </footer>
          </>
        )}

        {tab === "redeem" && !redeemed && (
          <form onSubmit={redeem} className={styles.redeemForm}>
            <p className={styles.hint}>{redeemHint}</p>
            <label className={styles.wellLabel} htmlFor="link-code-input">
              Enter code
            </label>
            <input
              id="link-code-input"
              className={styles.codeInput}
              value={codeInput}
              placeholder="XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            />
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <footer className={styles.footer}>
              <DialogCancel />
              <Button type="submit" loading={redeeming} disabled={codeInput.trim().length < 8}>
                Redeem code
              </Button>
            </footer>
          </form>
        )}

        {tab === "redeem" && redeemed && (
          <>
            <EmptyState title="Linked" description={redeemedNote} />
            <footer className={styles.footer}>
              <Button onClick={onClose}>Done</Button>
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
}
